import { MARKETPLACE_DEFAULTS } from './marketplace-defaults';
import { Marketplace } from './marketplace';
import { MarketplaceHelper } from './marketplace-helper';

/**
 * This class is based on the Java DefaultMarketplaceImpl,
 * providing default marketplace specific implementation.
 */
export class DefaultMarketplaceImpl extends Marketplace {
    private readonly obfuscatedMarketplaceId: undefined | string;
    private readonly defaultLocale: undefined | string;
    private readonly defaultCurrency: undefined | string;
    /**
     * This constructor enables consumers to override the default locale and default currency for the valid amazon
     * marketplace IDs.
     * If the default locale or default currency is passed as undefined for the valid obfuscatedMarketplaceId as key,
     * then the default locale or default currency will be retrieved from the MARKETPLACE_DEFAULTS mapping.
     * If it is a invalid amazon retail marketplace ID, and will have, the associated values will be assigned as undefined
     *
     * @param {undefined | string} obfuscatedMarketplaceIdParam - obfuscated marketplace id of the marketplace
     * @param {undefined | string} defaultLocaleParam - default locale of the marketplace
     * @param {undefined | string} defaultCurrencyParam - default currency of the marketplace
     */
    constructor(
        obfuscatedMarketplaceId?: undefined | string,
        defaultLocale?: undefined | string,
        defaultCurrency?: undefined | string
    ) {
        super();
        this.obfuscatedMarketplaceId = obfuscatedMarketplaceId;
        this.defaultCurrency = defaultCurrency;
        this.defaultLocale = defaultLocale;

        // do not attempt to resolve locale/currency if obfuscatedMarketplaceId is not specified
        if (!obfuscatedMarketplaceId) {
            return;
        }

        // resolve default locale or currency only when any is not specified
        if (!this.defaultLocale || !this.defaultCurrency) {
            // logic to resolve default locale and currency from marketplace default
            const cxTuple = MarketplaceHelper.fetchCxTuple(obfuscatedMarketplaceId);

            this.defaultLocale =
                this.defaultLocale || MarketplaceHelper.fetchDefaultLocale(cxTuple) || super.getDefaultLocale();
            this.defaultCurrency = this.defaultCurrency || MarketplaceHelper.fetchDefaultCurrency(cxTuple);
        }
    }

    /**
     * get the default obfuscatedMarketplaceId of the marketplace with default implementation behavior
     * @return {undefined | string} obfuscatedMarketplaceId - default obfuscatedMarketplaceId of the marketplace
     */
    public getObfuscatedMarketplaceId(): undefined | string {
        return this.obfuscatedMarketplaceId;
    }

    /**
     * get the default locale of the marketplace with default implementation behavior
     * @return {undefined | string} defaultLocale - default locale of the marketplace
     */
    public getDefaultLocale(): undefined | string {
        return this.defaultLocale;
    }

    /**
     * get the default currency of the marketplace with default implementation behavior
     * @return {undefined | string} defaultCurrency - default currency of the marketplace
     */
    public getDefaultCurrency(): undefined | string {
        return this.defaultCurrency;
    }

    public toString(): string {
        return `Marketplace[obfuscatedMarketplaceId=${this.getObfuscatedMarketplaceId()},defaultLocale=${this.getDefaultLocale()},defaultCurrency=${this.getDefaultCurrency()}]`;
    }

    /**
     * Returns a map of all Amazon Marketplaces and their corresponding ObfuscatedIds.
     * e.g. GB = A1F83G8C2ARO7P
     *      AE_DEVO = A34GYYCZVDBSIK
     *
     * @return {Map<string, string>} map of marketplaces and their obfuscated ids.
     */
    public static getObfuscatedMarketplaceIdMap(): Map<string, string> {
        const map = new Map<string, string>();
        for (const marketplaceId of Object.keys(MARKETPLACE_DEFAULTS)) {
            map.set(MARKETPLACE_DEFAULTS[marketplaceId].name, marketplaceId);
        }
        return map;
    }
}
